import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import LogicFrame from "./LogicFrame";
import { fetchPracticeProgram, fetchPracticeLog, addPracticeLog, addPattern, deletePattern } from "@/lib/logicApi";
import { useLogicUi } from "./logicUi";
import { BTN_FX, DISCIPLINE_THEME } from "@/lib/academyTheme";

const DAYS_30 = 30 * 24 * 3600 * 1000;

function PracticeCard({ p, logs, onLogged }) {
  const { ui, L } = useLogicUi();
  const th = DISCIPLINE_THEME.logic;
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const mine = logs.filter((l) => l.practiceId === p.id);
  const recent = mine.filter((l) => Date.now() - new Date(l.at).getTime() < DAYS_30).length;
  const last = mine[0]?.at;
  const record = async () => {
    setSaving(true);
    try { await addPracticeLog(p.id, note); setNote(""); setOpen(false); onLogged(); }
    finally { setSaving(false); }
  };
  return (
    <div className="border border-[#c8a95a]/20 bg-[#171c26] p-6" data-testid={`practice-card-${p.id}`}>
      <div className="flex items-start justify-between gap-4">
        <h3 className="font-serif text-lg text-bone">{L(p.title)}</h3>
        <span className="text-[10px] tracking-[0.2em] uppercase text-[#8fa3c8] whitespace-nowrap">{p.cadence}</span>
      </div>
      <p className="mt-3 text-sm text-fog font-reading leading-[1.85]">{L(p.instruction)}</p>
      <p className="mt-3 text-[13px] text-fog/60 font-reading italic leading-relaxed">{L(p.grounding)}</p>
      <div className="mt-5 pt-4 border-t border-white/10 flex flex-wrap items-center justify-between gap-3">
        <span className="text-[11px] text-fog/60" data-testid={`practice-stats-${p.id}`}>
          {mine.length === 0 ? ui.notRecorded : ui.practisedTimes(recent, String(last).slice(0, 10))}
        </span>
        {p.id === "full-record" ? (
          <Link to="/academy/logic/journal" className="text-[10px] tracking-[0.2em] uppercase text-gold hover:underline">{ui.openTheRecord}</Link>
        ) : (
          <button data-testid={`practice-record-${p.id}`} onClick={() => setOpen(!open)}
            className="text-[10px] tracking-[0.2em] uppercase text-gold border border-gold/40 px-3 py-1.5 hover:bg-gold/10">{ui.recordThis}</button>
        )}
      </div>
      {open && (
        <div className="mt-4 space-y-3">
          <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid={`practice-note-${p.id}`}
            placeholder={ui.practiceNotePh}
            className="w-full bg-[#f3e9d2]/[0.05] border border-[#c8a95a]/25 text-bone font-reading p-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/50" />
          <button data-testid={`practice-save-${p.id}`} onClick={record} disabled={saving}
            className={`px-5 py-2 text-[10px] font-semibold tracking-[0.2em] uppercase ${th.btn} ${BTN_FX} ${th.ring}`}>{ui.enter}</button>
        </div>
      )}
    </div>
  );
}

function SelfAudit({ logs, onLogged }) {
  const { ui } = useLogicUi();
  const th = DISCIPLINE_THEME.logic;
  const QUESTIONS = ui.auditQs;
  const [answers, setAnswers] = useState(["", "", "", ""]);
  const [open, setOpen] = useState(false);
  const audits = logs.filter((l) => l.practiceId === "self-audit");
  const save = async () => {
    const note = QUESTIONS.map((q, i) => `${q}\n${answers[i]}`).join("\n\n");
    await addPracticeLog("self-audit", note);
    setAnswers(["", "", "", ""]); setOpen(false); onLogged();
  };
  return (
    <div className="border border-gold/30 bg-[#f3e9d2]/[0.05] p-6" data-testid="self-audit-card">
      <h3 className="font-serif text-lg text-bone">{ui.auditTitle}</h3>
      <p className="mt-2 text-sm text-fog/70 font-reading">
        {audits.length === 0 ? ui.auditNone : `${ui.auditLast} ${String(audits[0].at).slice(0, 10)}.`} {ui.auditFour}
      </p>
      {!open ? (
        <button data-testid="self-audit-open" onClick={() => setOpen(true)}
          className="mt-4 text-[10px] tracking-[0.2em] uppercase text-gold border border-gold/40 px-4 py-2 hover:bg-gold/10">{ui.auditSit}</button>
      ) : (
        <div className="mt-5 space-y-4">
          {QUESTIONS.map((q, i) => (
            <div key={q}>
              <label className="block text-sm text-bone/90 font-reading mb-2">{q}</label>
              <textarea rows={2} value={answers[i]} data-testid={`self-audit-q${i}`}
                onChange={(e) => setAnswers((a) => a.map((x, j) => (j === i ? e.target.value : x)))}
                className="w-full bg-black/20 border border-[#c8a95a]/25 text-bone font-reading p-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/50" />
            </div>
          ))}
          <button data-testid="self-audit-save" onClick={save} disabled={answers.every((a) => !a.trim())}
            className={`px-6 py-3 text-[10px] font-semibold tracking-[0.2em] uppercase ${th.btn} ${BTN_FX} ${th.ring}`}>{ui.auditEnter}</button>
        </div>
      )}
      {audits.length > 0 && (
        <details className="mt-5 pt-4 border-t border-white/10">
          <summary className="text-[10px] tracking-[0.2em] uppercase text-[#8fa3c8] cursor-pointer">{ui.auditPrev} ({audits.length})</summary>
          <div className="mt-3 space-y-4">
            {audits.map((a) => (
              <div key={a.id} className="text-sm text-fog/80 font-reading whitespace-pre-line border-l-2 border-gold/30 pl-4">
                <p className="text-[10px] text-fog/50 mb-1">{String(a.at).slice(0, 10)}</p>{a.note}
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function Patterns({ patterns, onChanged }) {
  const { ui } = useLogicUi();
  const [text, setText] = useState("");
  return (
    <div className="border border-[#8fa3c8]/25 bg-[#1a2130] p-6" data-testid="patterns-card">
      <h3 className="font-serif text-lg text-bone">{ui.patternsTitle}</h3>
      <p className="mt-2 text-sm text-fog/70 font-reading leading-relaxed">
        {ui.patternsIntro}
      </p>
      <div className="mt-4 flex gap-3">
        <input value={text} onChange={(e) => setText(e.target.value)} data-testid="pattern-input"
          placeholder={ui.patternPh}
          className="flex-1 bg-black/20 border border-[#c8a95a]/25 text-bone font-reading px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold/50" />
        <button data-testid="pattern-add" onClick={async () => { if (text.trim()) { await addPattern(text); setText(""); onChanged(); } }}
          className="px-4 py-2 border border-gold/40 text-gold text-[10px] tracking-[0.2em] uppercase hover:bg-gold/10">{ui.nameIt}</button>
      </div>
      <ul className="mt-4 space-y-2" data-testid="patterns-list">
        {patterns.map((p) => (
          <li key={p.id} className="flex items-center justify-between gap-3 text-sm text-fog/90 font-reading border-b border-white/5 pb-2">
            <span>{p.text}</span>
            <button onClick={async () => { await deletePattern(p.id); onChanged(); }} aria-label="Remove pattern"
              className="text-fog/40 hover:text-red-300 text-xs">{ui.remove}</button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function PracticePage() {
  const { ui, L } = useLogicUi();
  const [program, setProgram] = useState(null);
  const [logs, setLogs] = useState([]);
  const [patterns, setPatterns] = useState([]);
  const load = () => fetchPracticeLog().then((d) => { setLogs(d.logs || []); setPatterns(d.patterns || []); });
  useEffect(() => { fetchPracticeProgram().then((d) => setProgram(d.program)).catch(() => {}); load(); }, []);
  if (!program) return <div className="min-h-screen bg-ink" />;
  return (
    <LogicFrame title={ui.practiceTitle} seoTitle="Practice Beyond the Academy" crumb={ui.practiceCrumb} testid="logic-practice-page">
      <p className="text-fog/90 font-reading max-w-2xl mb-14 leading-[1.9]">{L(program.intro)}</p>
      <div className="space-y-14">
        {program.sections.map((s) => (
          <section key={s.id} aria-label={L(s.title)}>
            <h2 className="text-xs tracking-[0.3em] uppercase text-gold/80 mb-6">{L(s.title)}</h2>
            <div className="grid md:grid-cols-2 gap-5">
              {s.practices.map((p) => p.id === "self-audit"
                ? <SelfAudit key={p.id} logs={logs} onLogged={load} />
                : <PracticeCard key={p.id} p={p} logs={logs} onLogged={load} />)}
            </div>
          </section>
        ))}
        <Patterns patterns={patterns} onChanged={load} />
      </div>
    </LogicFrame>
  );
}
